import { useCallback, useEffect, useState } from "react";
import { api, errDetail, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { KeyRound, Plus, Mail, Ban, CheckCircle2, Trash2, ShieldCheck } from "lucide-react";

const STATUS = { active: ["bg-green-100 text-green-700", "Aktiv"], invited: ["bg-amber-100 text-amber-800", "Eingeladen"], disabled: ["bg-gray-200 text-gray-600", "Deaktiviert"] };

export default function SupplierPortalAdmin() {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const load = useCallback(() => { api.get("/supplier-portal-users").then((r) => setRows(r.data || [])).catch((e) => toast.error(errDetail(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => { try { await api.post(`/supplier-portal-users/${id}/resend-invite`); toast.success("Einladung erneut gesendet"); load(); } catch (e) { toast.error(errDetail(e)); } };
  const toggle = async (u) => { try { await api.post(`/supplier-portal-users/${u.id}/toggle`, { disabled: u.status !== "disabled" }); load(); } catch (e) { toast.error(errDetail(e)); } };
  const del = async (id) => { try { await api.delete(`/supplier-portal-users/${id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errDetail(e)); } };

  return (
    <div className="p-6 max-w-[1100px] mx-auto" data-testid="sp-admin-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><KeyRound className="h-5 w-5 text-[#F5A623]" /></div>
          <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Lieferantenportal – Zugänge</h1><p className="text-sm text-gray-500">Einladung · Passwort · TOTP-MFA</p></div>
        </div>
        <Button className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setAddOpen(true)} data-testid="sp-admin-invite-btn"><Plus className="h-4 w-4 mr-1" />Einladen</Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>E-Mail</TableHead><TableHead>Lieferant</TableHead><TableHead>Kontakt</TableHead><TableHead>Status</TableHead><TableHead>MFA</TableHead><TableHead>Erstellt</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Keine Portal-Zugänge</TableCell></TableRow>}
            {rows.map((u) => {
              const [cls, lbl] = STATUS[u.status] || ["bg-gray-100", u.status];
              return (
                <TableRow key={u.id} data-testid={`sp-admin-row-${u.email}`}>
                  <TableCell className="font-medium">{u.email}</TableCell>
                  <TableCell>{u.supplier_name}</TableCell>
                  <TableCell>{u.contact_name || "–"}</TableCell>
                  <TableCell><Badge className={cls}>{lbl}</Badge></TableCell>
                  <TableCell>{u.mfa_enabled ? <ShieldCheck className="h-4 w-4 text-green-600" /> : <span className="text-xs text-gray-400">–</span>}</TableCell>
                  <TableCell className="text-sm text-gray-500">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => resend(u.id)} data-testid="sp-admin-resend"><Mail className="h-3 w-3 mr-1" />Einladung</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toggle(u)} data-testid="sp-admin-toggle">
                      {u.status === "disabled" ? <><CheckCircle2 className="h-3 w-3 mr-1" />Aktivieren</> : <><Ban className="h-3 w-3 mr-1" />Sperren</>}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => del(u.id)} data-testid="sp-admin-delete"><Trash2 className="h-3 w-3" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {addOpen && <InviteDialog onClose={(ch) => { setAddOpen(false); if (ch) load(); }} />}
    </div>
  );
}

function InviteDialog({ onClose }) {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ email: "", supplier_id: "", contact_name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {}); }, []);
  const save = async () => {
    if (!form.email || !form.supplier_id) { toast.error("E-Mail und Lieferant erforderlich"); return; }
    setBusy(true);
    try { await api.post("/supplier-portal-users", form); toast.success("Einladung gesendet"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="sp-invite-dialog">
        <DialogHeader><DialogTitle>Lieferant einladen</DialogTitle><DialogDescription>Sendet eine Einladung per E-Mail zur Einrichtung von Passwort + Authenticator-App.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Lieferant *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
              <SelectTrigger data-testid="sp-invite-supplier"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>E-Mail *</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="sp-invite-email" /></div>
          <div><Label>Kontaktname</Label><Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="sp-invite-contact" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button className="bg-[#1B2A4A] hover:bg-[#24365d]" disabled={busy} onClick={save} data-testid="sp-invite-save">Einladen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
